import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: string | null }>;
  signInWithGoogle: () => Promise<{ error: string | null }>;
  signInAnonymously: () => Promise<{ error: string | null; user: User | null }>;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  session: null,
  loading: true,

  initialize: async () => {
    const { data } = await supabase.auth.getSession();
    set({
      session: data.session,
      user: data.session?.user ?? null,
      loading: false,
    });
    supabase.auth.onAuthStateChange((_event, session) => {
      set({ session, user: session?.user ?? null });
    });
  },

  signIn: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    set({ session: data.session, user: data.user });
    return { error: null };
  },

  signUp: async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: fullName ? { full_name: fullName } : undefined,
      },
    });
    if (error) return { error: error.message };
    set({ session: data.session, user: data.user });
    return { error: null };
  },

  // Google OAuth flow:
  //   1. We tell Supabase to start the OAuth handshake.
  //   2. Supabase redirects the browser to Google.
  //   3. Google sends the user back to /auth/callback with a code.
  //   4. Supabase exchanges the code for a session and triggers
  //      onAuthStateChange (set up in initialize).
  // Note: this function navigates away — the caller's UI never gets
  // control back unless an error occurs before the redirect.
  signInWithGoogle: async () => {
    const redirectTo = `${window.location.origin}/auth/callback`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        queryParams: {
          // Hint Google to always show account picker, in case user is
          // logged into multiple Google accounts.
          prompt: 'select_account',
        },
      },
    });
    if (error) return { error: error.message };
    return { error: null };
  },

  // Anonymous sign-in for the 15-second onboarding flow.
  // Supabase creates a real auth.users row with is_anonymous=true and
  // issues a JWT. Our sync_profile_anonymous_flag trigger then sets
  // is_anonymous=true on the matching profiles row.
  //
  // The user_id we get here persists if the user later converts to a
  // real account via supabase.auth.updateUser({ email, password }) or
  // linkIdentity — meaning all their babies, messages, sessions etc.
  // carry over to the linked account with zero migration.
  signInAnonymously: async () => {
    const { data, error } = await supabase.auth.signInAnonymously();
    if (error) return { error: error.message, user: null };
    set({ session: data.session, user: data.user });
    return { error: null, user: data.user };
  },

  signOut: async () => {
    await supabase.auth.signOut();
    set({ session: null, user: null });
  },
}));
